/**
 * Repair Wizard Shared State
 * Manages all state, navigation, and logic for the 4-step repair wizard.
 * Used by restauracion.vue (layout shell) and restauracion/*.vue (child pages).
 */
import type { DeviceCategory, DeviceModel } from '~/composables/useDeviceSelector'
import type { RepairCategoryItem, RepairServiceItem, RepairStoreItem } from '~/composables/useRepairAPI'
import type { UserAddress } from '~/composables/useAddressAPI'
import { apiPost } from '~/utils/api'

const STORAGE_KEY = 'midigi_repair_wizard'
const TOTAL_STEPS = 4

// Category icon mapping (pure constant, no reactivity).
const categoryIcons: Record<string, string> = {
  iphone: 'i-lucide-smartphone',
  ipad: 'i-lucide-tablet',
  mac: 'i-lucide-laptop',
  watch: 'i-lucide-watch',
}

export function useRepairWizard() {
  // These guards belong to the component instance that owns initWizard().
  // Module-level guards outlive Vue's effect scope: after SPA navigation the
  // old watchers are disposed, while the stale guards used to prevent the
  // newly-mounted repair page from registering replacements or reloading data.
  let initPromise: Promise<void> | null = null
  let watchersRegistered = false

  // Singleton state — useState() with a string key returns the same ref
  // across every caller, so moving these inside the factory keeps state
  // shared while ensuring they're only resolved within a Vue setup /
  // plugin context (was previously crashing at module load time).
  const isRestoring = useState('rw_isRestoring', () => false)
  const isLoading = useState('rw_isLoading', () => false)
  const isSubmitting = useState('rw_isSubmitting', () => false)

  // Step 1: Device category + model
  const deviceCategories = useState<DeviceCategory[]>('rw_deviceCategories', () => [])
  const selectedCategoryCode = useState('rw_selectedCategoryCode', () => '')
  const deviceModels = useState<DeviceModel[]>('rw_deviceModels', () => [])
  const allDeviceModels = useState<DeviceModel[]>('rw_allDeviceModels', () => []) // all models across categories for search
  const selectedModel = useState<DeviceModel | null>('rw_selectedModel', () => null)
  const modelSearchQuery = useState('rw_modelSearchQuery', () => '')

  // Step 2: Repair services
  const repairCategories = useState<RepairCategoryItem[]>('rw_repairCategories', () => [])
  const repairCategoriesModelId = useState<string | null>('rw_repairCategoriesModelId', () => null)
  const repairCategoriesRequestId = useState('rw_repairCategoriesRequestId', () => 0)
  const isLoadingRepairCategories = useState('rw_isLoadingRepairCategories', () => false)
  const selectedServiceIds = useState<string[]>('rw_selectedServiceIds', () => [])

  // Step 3: Service type + location + schedule
  const selectedServiceType = useState('rw_selectedServiceType', () => '')
  const stores = useState<RepairStoreItem[]>('rw_stores', () => [])
  const addresses = useState<UserAddress[]>('rw_addresses', () => [])
  const selectedStoreId = useState('rw_selectedStoreId', () => '')
  const selectedAddressId = useState('rw_selectedAddressId', () => '')
  const scheduledDate = useState('rw_scheduledDate', () => '')
  const scheduledTimeSlot = useState('rw_scheduledTimeSlot', () => '')

  // Step 4: Device notes (optional)
  const deviceCondition = useState('rw_deviceCondition', () => '')
  const customerNotes = useState('rw_customerNotes', () => '')
  const couponCode = useState('rw_couponCode', () => '')

  const initialized = useState('rw_initialized', () => false)

  const { t } = useI18n()
  const localePath = useLocalePath()
  const { formatPrice } = useShopUtils()
  const { isLoggedIn } = useAuth()
  const router = useRouter()
  const route = useRoute()
  const toast = useDSToast()

  const { fetchDeviceCategories, fetchDeviceModels } = useDeviceSelector()
  const {
    error: repairCatalogError,
    fetchCategories: fetchRepairCategories,
    fetchStores,
    getEffectivePrice,
  } = useRepairAPI()
  const { fetchAddresses } = useAddressAPI()

  // ==================== Step from URL ====================

  const currentStep = computed(() => {
    const match = route.path.match(/step-(\d+)/)
    if (match) return parseInt(match[1])
    return 1
  })

  // ==================== Computed ====================

  const selectedServices = computed(() => {
    const services: (RepairServiceItem & { categoryName: string })[] = []
    for (const cat of repairCategories.value) {
      for (const svc of cat.services) {
        if (selectedServiceIds.value.includes(svc.id)) {
          services.push({ ...svc, categoryName: cat.name })
        }
      }
    }
    return services
  })

  const totalPrice = computed(() => {
    return selectedServices.value.reduce((sum, svc) => sum + getEffectivePrice(svc), 0)
  })

  const durationRange = computed(() => {
    const totalMins = selectedServices.value.reduce((sum, svc) => sum + svc.durationMins, 0)
    const minDays = Math.max(1, Math.ceil(totalMins / 480))
    const maxDays = minDays + 1
    return { min: minDays, max: maxDays }
  })

  const maxWarranty = computed(() => {
    return Math.max(0, ...selectedServices.value.map(s => s.warrantyMonths))
  })

  const selectedModelLabel = computed(() => selectedModel.value?.name || '')

  // Filtered models: search filters across all models, category filters by code
  const filteredModels = computed(() => {
    const query = modelSearchQuery.value.trim().toLowerCase()
    if (query) {
      // Search across all models regardless of category
      return allDeviceModels.value.filter(m => m.name.toLowerCase().includes(query))
    }
    return deviceModels.value
  })

  // Search results for dropdown
  const searchResults = computed(() => {
    const query = modelSearchQuery.value.trim().toLowerCase()
    if (!query) return []
    return allDeviceModels.value.filter(m => m.name.toLowerCase().includes(query)).slice(0, 8)
  })

  function selectSearchResult(model: DeviceModel) {
    selectedModel.value = model
    modelSearchQuery.value = ''
    // Also switch to the model's category
    if (model.categoryCode && model.categoryCode !== selectedCategoryCode.value) {
      isRestoring.value = true
      selectedCategoryCode.value = model.categoryCode
      nextTick(() => { isRestoring.value = false })
    }
  }

  const hasLocation = computed(() => {
    if (!selectedServiceType.value) return false
    if (selectedServiceType.value === 'in_store') return !!selectedStoreId.value
    return !!selectedAddressId.value
  })

  const weekDayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

  function isStoreOpenOnDate(dateValue: string): boolean {
    if (!selectedStoreId.value) return false
    const store = stores.value.find(item => item.id === selectedStoreId.value)
    // Keep restored selections until the store request finishes; the
    // computed dates will be recalculated once the store is available.
    if (!store) return true

    const businessHours = store.businessHours || {}
    // Stores without configured hours keep the previous behavior and allow
    // date selection; configured closed days are filtered out below.
    if (Object.keys(businessHours).length === 0) return true

    const date = new Date(`${dateValue}T12:00:00`)
    if (Number.isNaN(date.getTime())) return false
    const hours = businessHours[weekDayKeys[date.getDay()]]?.trim() || ''
    return Boolean(hours) && !/^(休息|休息日|closed|off|day off)$/i.test(hours)
  }

  const canProceed = computed(() => {
    switch (currentStep.value) {
      case 1: return !!selectedModel.value
      case 2: return selectedServices.value.length > 0 && !isLoadingRepairCategories.value
      case 3: return hasLocation.value && (
        selectedServiceType.value === 'mail_in'
          || (!!scheduledDate.value && !!scheduledTimeSlot.value && isStoreOpenOnDate(scheduledDate.value))
      )
      case 4: return selectedServices.value.length > 0 && !isLoadingRepairCategories.value
      default: return false
    }
  })

  const stepLabels = computed(() => [
    t('repairPage.wizard.selectDevice'),
    t('repairPage.wizard.selectRepairs'),
    t('repairPage.wizard.serviceSchedule'),
    t('repairPage.wizard.reviewSubmit'),
  ])

  // Service type options
  const serviceTypeOptions = computed(() => [
    { value: 'in_store', icon: 'i-lucide-store', titleKey: 'repairPage.serviceType.in_store.title', descKey: 'repairPage.serviceType.in_store.desc' },
    { value: 'mail_in', icon: 'i-lucide-package', titleKey: 'repairPage.serviceType.mail_in.title', descKey: 'repairPage.serviceType.mail_in.desc' },
  ])

  // Time slot options
  const timeSlotOptions = [
    { value: 'morning', key: 'repairPage.timeSlots.morning' },
    { value: 'afternoon', key: 'repairPage.timeSlots.afternoon' },
    { value: 'full_day', key: 'repairPage.timeSlots.fullDay' },
  ]

  // ==================== Navigation ====================

  async function focusWizard() {
    if (!import.meta.client) return
    await nextTick()
    await nextTick()
    await new Promise<void>((resolve) => {
      window.setTimeout(() => {
        const wizard = document.getElementById('repair-wizard-progress')
        if (wizard) {
          wizard.focus({ preventScroll: true })
          const top = wizard.getBoundingClientRect().top + window.scrollY - 16
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
          window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' })
        }
        resolve()
      }, 0)
    })
  }

  async function navigateToStep(step: number) {
    if (step < 1 || step > TOTAL_STEPS) return
    if (step === 1) {
      await router.push(localePath('/restauracion'))
    } else {
      await router.push(localePath(`/restauracion/step-${step}`))
    }
  }

  async function nextStep() {
    if (!canProceed.value) return

    const next = currentStep.value + 1

    // Auth gate: step 3 → 4 requires login
    if (next === 4 && !isLoggedIn.value) {
      redirectToLogin()
      return
    }

    saveWizardState()
    await navigateToStep(next)
  }

  async function prevStep() {
    if (currentStep.value > 1) {
      saveWizardState()
      await navigateToStep(currentStep.value - 1)
    }
  }

  // ==================== Data Loading ====================

  async function loadLocationData() {
    isLoading.value = true
    try {
      if (selectedServiceType.value === 'in_store') {
        stores.value = await fetchStores()
      } else if (isLoggedIn.value) {
        addresses.value = await fetchAddresses()
      }
    } finally {
      isLoading.value = false
    }
  }

  // ==================== Actions ====================

  function toggleService(serviceId: string) {
    const idx = selectedServiceIds.value.indexOf(serviceId)
    if (idx === -1) {
      selectedServiceIds.value.push(serviceId)
    } else {
      selectedServiceIds.value.splice(idx, 1)
    }
  }

  async function loadRepairCategoriesForModel(modelId: string) {
    const requestId = ++repairCategoriesRequestId.value
    isLoadingRepairCategories.value = true
    const isRefreshingCurrentModel = repairCategoriesModelId.value === modelId
    if (!isRefreshingCurrentModel) {
      repairCategories.value = []
      repairCategoriesModelId.value = null
    }

    try {
      const categories = await fetchRepairCategories(modelId)
      if (
        !repairCatalogError.value
        && repairCategoriesRequestId.value === requestId
        && selectedModel.value?.id === modelId
      ) {
        repairCategories.value = categories
        repairCategoriesModelId.value = modelId
        const availableServiceIds = new Set(
          categories.flatMap(category => category.services.map(service => service.id)),
        )
        selectedServiceIds.value = selectedServiceIds.value
          .filter(serviceId => availableServiceIds.has(serviceId))
      }
    } finally {
      if (repairCategoriesRequestId.value === requestId) {
        isLoadingRepairCategories.value = false
      }
    }
  }

  // Ensures repair categories are loaded for the selected model. Defensive
  // against races where the selectedModel watcher was suppressed (isRestoring)
  // or the Continue click happened before the in-flight fetch completed.
  async function ensureRepairCategoriesLoaded(force = false) {
    if (!selectedModel.value) return
    const modelId = selectedModel.value.id
    if (!force && repairCategoriesModelId.value === modelId) return
    if (isLoadingRepairCategories.value) return
    await loadRepairCategoriesForModel(modelId)
  }

  async function selectServiceType(type: string) {
    // Auth gate: on_site/mail_in requires login
    if (type !== 'in_store' && !isLoggedIn.value) {
      redirectToLogin()
      return
    }
    selectedServiceType.value = type
    // Reset location selections
    selectedStoreId.value = ''
    selectedAddressId.value = ''
    scheduledDate.value = ''
    scheduledTimeSlot.value = ''
    // Load location data
    await loadLocationData()
  }

  async function submitOrder() {
    if (isSubmitting.value) return
    isSubmitting.value = true
    try {
      const body = {
        productId: selectedModel.value?.id,
        serviceType: selectedServiceType.value,
        storeId: selectedServiceType.value === 'in_store' ? selectedStoreId.value : undefined,
        addressId: selectedServiceType.value !== 'in_store' ? selectedAddressId.value : undefined,
        scheduledDate: selectedServiceType.value === 'in_store' ? (scheduledDate.value || undefined) : undefined,
        scheduledTimeSlot: selectedServiceType.value === 'in_store' ? (scheduledTimeSlot.value || undefined) : undefined,
        items: selectedServices.value.map(service => ({
          serviceId: service.id,
          quantity: 1,
          expectedUnitPrice: getEffectivePrice(service),
        })),
        deviceCondition: deviceCondition.value || undefined,
        customerNotes: customerNotes.value || undefined,
        couponCode: couponCode.value?.trim() ? couponCode.value.trim().toUpperCase() : undefined,
      }
      const result = await apiPost('/api/app/v1/orders/repair', body)
      clearWizardData()
      toast.success(t('repairPage.orderSuccess'))
      router.push(localePath(`/cuenta/repair-orders/${result.id}`))
    } catch (e: unknown) {
      // The backend rejects stale reviewed prices. Refresh the quote before
      // showing the error so the next confirmation displays the current price.
      await ensureRepairCategoriesLoaded(true)
      if (selectedServiceIds.value.length === 0) {
        toast.warning(t('repairPage.servicesChanged'))
        await navigateToStep(2)
      }
      toast.error(e instanceof Error ? e.message : t('repairPage.orderError'))
    } finally {
      isSubmitting.value = false
    }
  }

  // ==================== Session Persistence ====================

  function saveWizardState() {
    const state = {
      currentStep: currentStep.value,
      selectedCategoryCode: selectedCategoryCode.value,
      selectedModel: selectedModel.value,
      selectedServiceIds: selectedServiceIds.value,
      selectedServiceType: selectedServiceType.value,
      selectedStoreId: selectedStoreId.value,
      selectedAddressId: selectedAddressId.value,
      scheduledDate: scheduledDate.value,
      scheduledTimeSlot: scheduledTimeSlot.value,
      deviceCondition: deviceCondition.value,
      customerNotes: customerNotes.value,
      couponCode: couponCode.value,
    }
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)) } catch { /* ignore */ }
  }

  function clearWizardState() {
    try { sessionStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
  }

  async function restoreWizardState(): Promise<number | false> {
    let raw: string | null = null
    try { raw = sessionStorage.getItem(STORAGE_KEY) } catch { return false }
    if (!raw) return false

    try {
      const state = JSON.parse(raw)

      // Compat: old 8-step data → discard
      if (state.currentStep > TOTAL_STEPS) {
        clearWizardState()
        return false
      }

      if (!state.selectedCategoryCode || !state.currentStep) {
        clearWizardState()
        return false
      }

      isRestoring.value = true

      // Restore simple refs
      selectedCategoryCode.value = state.selectedCategoryCode || ''
      selectedModel.value = state.selectedModel || null
      selectedServiceIds.value = state.selectedServiceIds || []
      selectedServiceType.value = state.selectedServiceType || ''
      selectedStoreId.value = state.selectedStoreId || ''
      selectedAddressId.value = state.selectedAddressId || ''
      scheduledDate.value = state.scheduledDate || ''
      scheduledTimeSlot.value = state.scheduledTimeSlot || ''
      if (selectedServiceType.value !== 'in_store') {
        scheduledDate.value = ''
        scheduledTimeSlot.value = ''
      }
      deviceCondition.value = state.deviceCondition || ''
      customerNotes.value = state.customerNotes || ''
      couponCode.value = state.couponCode || ''

      // Re-fetch dependent API data
      deviceCategories.value = await fetchDeviceCategories()

      // Load all models for search
      const allModels: DeviceModel[] = []
      for (const cat of deviceCategories.value) {
        const models = await fetchDeviceModels(cat.code)
        allModels.push(...models)
      }
      allDeviceModels.value = allModels

      if (selectedCategoryCode.value) {
        deviceModels.value = await fetchDeviceModels(selectedCategoryCode.value)
      }
      if (selectedModel.value) {
        await loadRepairCategoriesForModel(selectedModel.value.id)
      }
      // Stores/addresses for step 3+
      if (state.currentStep >= 3 && selectedServiceType.value) {
        if (selectedServiceType.value === 'in_store') {
          stores.value = await fetchStores()
        } else if (isLoggedIn.value) {
          addresses.value = await fetchAddresses()
        }
      }

      clearWizardState()
      isRestoring.value = false
      return state.currentStep as number
    } catch {
      clearWizardState()
      isRestoring.value = false
      return false
    }
  }

  function redirectToLogin() {
    saveWizardState()
    router.push(localePath('/login?redirect=/restauracion'))
  }

  // ==================== Reset ====================

  // 清空向导所有状态（不导航）。下单成功后调用，避免上一单的字段（含 couponCode）残留到下一单。
  function clearWizardData() {
    selectedCategoryCode.value = ''
    selectedModel.value = null
    deviceModels.value = []
    modelSearchQuery.value = ''
    repairCategoriesRequestId.value++
    repairCategories.value = []
    repairCategoriesModelId.value = null
    isLoadingRepairCategories.value = false
    selectedServiceIds.value = []
    selectedServiceType.value = ''
    stores.value = []
    addresses.value = []
    selectedStoreId.value = ''
    selectedAddressId.value = ''
    scheduledDate.value = ''
    scheduledTimeSlot.value = ''
    deviceCondition.value = ''
    customerNotes.value = ''
    couponCode.value = ''
    clearWizardState()
  }

  function resetWizard() {
    clearWizardData()
    router.push(localePath('/restauracion'))
  }

  // ==================== Watchers ====================

  function registerWatchers() {
    if (watchersRegistered) return
    watchersRegistered = true

    // Category change → load models
    watch(selectedCategoryCode, async (code) => {
      if (isRestoring.value) return
      selectedModel.value = null
      selectedServiceIds.value = []
      repairCategoriesRequestId.value++
      repairCategories.value = []
      repairCategoriesModelId.value = null
      isLoadingRepairCategories.value = false
      if (!code) return
      isLoading.value = true
      try {
        deviceModels.value = await fetchDeviceModels(code)
      } finally {
        isLoading.value = false
      }
    })

    // Model change → pre-load repair services (silent, no loading overlay)
    watch(selectedModel, async (model) => {
      if (isRestoring.value) return
      selectedServiceIds.value = []
      repairCategoriesRequestId.value++
      repairCategories.value = []
      repairCategoriesModelId.value = null
      isLoadingRepairCategories.value = false
      if (!model) return
      await loadRepairCategoriesForModel(model.id)
    })

    // Route navigation resets the document scroll position. Keep the user's
    // focus on the repair wizard when using next/previous or browser history.
    watch(currentStep, async (step, previousStep) => {
      if (previousStep === undefined || step === previousStep || !import.meta.client) return
      await focusWizard()
    })
  }

  // ==================== Step Guard ====================

  function guardStep(step: number): boolean {
    if (step === 1) return true
    if (step === 2) return !!selectedModel.value
    if (step === 3) return selectedServiceIds.value.length > 0
    if (step === 4) return selectedServiceIds.value.length > 0
    return false
  }

  // ==================== Initialization ====================

  function initWizard(): Promise<void> | undefined {
    if (!import.meta.client) return
    if (initPromise) return initPromise

    initPromise = (async () => {
      registerWatchers()
      initialized.value = false
      isLoading.value = true

      try {
        const restoredStep = await restoreWizardState()
        if (restoredStep) {
          const target = restoredStep === 1 ? '/restauracion' : `/restauracion/step-${restoredStep}`
          await router.replace(localePath(target))
          return
        }

        deviceCategories.value = await fetchDeviceCategories()

        // Load all models across categories for search. Populate the selected
        // category directly from this result instead of relying on an async
        // watcher that may run after the empty-state has already rendered.
        const allModels: DeviceModel[] = []
        for (const cat of deviceCategories.value) {
          const models = await fetchDeviceModels(cat.code)
          allModels.push(...models)
        }
        allDeviceModels.value = allModels

        const categoryStillAvailable = deviceCategories.value.some(
          category => category.code === selectedCategoryCode.value,
        )
        const categoryCode = categoryStillAvailable
          ? selectedCategoryCode.value
          : (deviceCategories.value[0]?.code || '')

        isRestoring.value = true
        try {
          selectedCategoryCode.value = categoryCode
          deviceModels.value = categoryCode
            ? allModels.filter(model => model.categoryCode === categoryCode)
            : []
        } finally {
          isRestoring.value = false
        }
      } finally {
        isLoading.value = false
        initialized.value = true
        initPromise = null
      }
    })()

    return initPromise
  }

  return {
    // Constants
    totalSteps: TOTAL_STEPS,
    categoryIcons,

    // State
    isRestoring,
    isLoading,
    isSubmitting,
    initialized,

    // Step 1
    deviceCategories,
    selectedCategoryCode,
    deviceModels,
    selectedModel,
    modelSearchQuery,
    filteredModels,
    searchResults,
    selectSearchResult,

    // Step 2
    repairCategories,
    isLoadingRepairCategories,
    selectedServiceIds,

    // Step 3
    selectedServiceType,
    stores,
    addresses,
    selectedStoreId,
    selectedAddressId,
    scheduledDate,
    scheduledTimeSlot,
    isStoreOpenOnDate,

    // Step 4
    deviceCondition,
    customerNotes,
    couponCode,

    // Computed
    currentStep,
    selectedServices,
    totalPrice,
    durationRange,
    maxWarranty,
    selectedModelLabel,
    hasLocation,
    canProceed,
    stepLabels,
    serviceTypeOptions,
    timeSlotOptions,

    // Navigation
    navigateToStep,
    focusWizard,
    nextStep,
    prevStep,

    // Actions
    toggleService,
    ensureRepairCategoriesLoaded,
    selectServiceType,
    submitOrder,
    resetWizard,
    initWizard,
    guardStep,
    saveWizardState,
    redirectToLogin,

    // Utils re-exported for child pages
    getEffectivePrice,
    formatPrice,
    t,
    localePath,
    isLoggedIn,
  }
}
